import type { HostDeclaredTool } from "@magnus/contract/host-tools";
import type { TurnOptions } from "@magnus/contract/turn";

/**
 * Provider-level defaults merged into every chat request body by
 * `useMagnus`. Pure and provider-agnostic so the merge semantics are
 * unit-testable without rendering the hook.
 */
export type TurnBodyDefaults = {
  /** Declarations for the provider's host-invented tools — those carrying
   *  their own description + inputSchema (ADR-096).
   *  Omitted from the body when empty; a per-send `getBody` value overrides
   *  them. */
  hostTools?: HostDeclaredTool[];
  /** The provider's `surface` — always sent as `appSurface`. */
  surface: string;
  surfaceContext?: Record<string, unknown>;
  /** The provider's `surfacePromptAppendix` (plan 032 §B2). Omitted from the
   *  body when unset; a per-send `getBody` value overrides it. */
  surfacePromptAppendix?: string;
  /** The runtime's IANA zone (`useMagnus` reads it from `Intl`). It is a fact
   *  about the environment, not a host decision, so the SDK supplies it for
   *  every surface rather than relying on each host to remember. Without it
   *  the server renders `<sent_at>` stamps in its own zone (UTC) while telling
   *  the model they're local. Omitted when unresolvable; a per-send `getBody`
   *  value overrides it. */
  timeZone?: string;
};

/**
 * The browser's IANA zone, or `undefined` where `Intl` can't resolve one
 * (non-browser runtimes, locked-down embeds). Never throws.
 */
export function resolveRuntimeTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build a chat request body from the provider defaults plus the caller's
 * per-send dynamic options. Dynamic fields win — a `getBody` can override
 * `appSurface`, `surfaceContext`, `surfacePromptAppendix`, and `timeZone`
 * per turn.
 */
export function buildTurnBody(
  defaults: TurnBodyDefaults,
  dynamic: TurnOptions = {}
): Record<string, unknown> {
  return {
    appSurface: defaults.surface,
    ...(defaults.hostTools?.length ? { hostTools: defaults.hostTools } : {}),
    ...(defaults.surfaceContext
      ? { surfaceContext: defaults.surfaceContext }
      : {}),
    ...(defaults.surfacePromptAppendix
      ? { surfacePromptAppendix: defaults.surfacePromptAppendix }
      : {}),
    ...(defaults.timeZone ? { timeZone: defaults.timeZone } : {}),
    ...dynamic,
  };
}
